import { itemKey } from "@examspark/common";
import { getRestApiUrl } from "@examspark/common/dist/api";
import { DocumentClient } from "@examspark/common-aws";
import { IDSK, IdskInput, Maybe, Resource } from "@examspark/types";

import {
  AmplifyEnvironment,
  batchGet,
  queryAllResourcesById,
  stringifyItemKey,
} from ".";

export const listRelationships = async (
  env: AmplifyEnvironment,
  id: string,
  relType: string,
) => {
  const rels = await queryAllResourcesById<Resource>(
    env.getDynamoDBDocumentClient(),
    {
      tableName: env.getTableName("Resource"),
      id: id,
      sortKeyBeginsWith: `${relType}:`,
    },
  );

  return rels;
};

export const putRelationship = async (
  client: DocumentClient,
  TableName: string,
  from: IdskInput,
  to: IdskInput,
  workspaceId: string,
  options?: {
    fromTypeName?: string;
    toTypeName?: string;
  },
) => {
  const now = new Date().toISOString();
  const fromKey = {
    id: from.id,
    sk: getRelationshipSortKey(from, to, options),
  };
  const toKey = {
    id: to.id,
    sk: getRelationshipSortKey(
      to,
      from,
      // left-right special-type-name swap
      options?.toTypeName && options?.fromTypeName
        ? {
            fromTypeName: options.toTypeName,
            toTypeName: options.fromTypeName,
          }
        : undefined,
    ),
  };

  // This operation has a high probablity of stepping on similar attempts to
  // create the exact same relationship at exactly the same time, especially
  // when indexing related content (ex: all items from a single passage;
  // indexing one unit that belongs to a course, that has lots of units; etc).
  // This has been known to cause lots of TransactionConflict exceptions which
  // risks index integrity. Let's check to make sure the relationship actually
  // needs to be created before we try to create it.
  const existing = await batchGet(client, TableName, [fromKey, toKey]);
  if (existing.length === 2) return;

  return client.transactWrite({
    TransactItems: [
      {
        Put: {
          TableName,
          Item: {
            ...fromKey,
            workspaceId: workspaceId,
            resourceId: formatResourceIdFieldValue(to),
            schemaVersion: "2020-12-01",
            createdAt: now,
            updatedAt: now,
            __typename: "Resource",
          },
        },
      },
      {
        Put: {
          TableName,
          Item: {
            ...toKey,
            resourceId: formatResourceIdFieldValue(from),
            workspaceId: workspaceId,
            schemaVersion: "2020-12-01",
            createdAt: now,
            updatedAt: now,
            __typename: "Resource",
          },
        },
      },
    ],
  });
};

export const deleteRelationship = async (
  client: DocumentClient,
  TableName: string,
  from: IdskInput,
  to: IdskInput,
  options?: {
    fromTypeName?: string;
    toTypeName?: string;
    dryrun?: boolean;
    verbose?: boolean;
  },
) => {
  const fromKey = {
    id: from.id,
    sk: getRelationshipSortKey(from, to, options),
  };

  const toKey = {
    id: to.id,
    sk: getRelationshipSortKey(
      to,
      from,
      // left-right special-type-name swap
      options?.toTypeName && options?.fromTypeName
        ? {
            fromTypeName: options.toTypeName,
            toTypeName: options.fromTypeName,
          }
        : undefined,
    ),
  };

  if (options?.dryrun || options?.verbose) {
    console.log(
      `${options.dryrun ? "[DRYRUN] " : ""}DELETE`,
      stringifyItemKey(fromKey),
    );
    console.log(
      `${options.dryrun ? "[DRYRUN] " : ""}DELETE`,
      stringifyItemKey(toKey),
    );
  }

  if (options?.dryrun) return;

  const result = await client.transactWrite({
    TransactItems: [
      {
        Delete: {
          TableName,
          Key: fromKey,
        },
      },
      {
        Delete: {
          TableName,
          Key: toKey,
        },
      },
    ],
  });

  return result;
};

const getRelationshipSortKey = (
  from: IdskInput,
  to: IdskInput,
  options?: {
    fromTypeName?: string;
    toTypeName?: string;
  },
) => {
  const leftType = options?.fromTypeName ?? from.sk.split("#")[0];
  const rightType = options?.toTypeName ?? to.sk.split("#")[0];

  return `${leftType}-${rightType}:${to.id}/${to.sk}`;
};

export const parseRelationshipSortKey = (sk: string) => {
  if (sk.split(":").length !== 2) {
    throw new Error(`Not a valid sort key: ${sk}`);
  }

  const [leftType, rightType] = sk.split(":")[0].split("-");
  const [rightId, rightSk] = sk.split(":")[1].split("/");

  return {
    fromType: leftType,
    toType: rightType,
    toId: rightId,
    toSk: rightSk,
  };
};

export const parseResourceId = (resourceId: string): IDSK => {
  if (!resourceId.includes("/")) {
    throw new Error(`Not a valid resource ID: ${resourceId}`);
  }
  const [id, ...sk] = resourceId.split("/");
  return { id, sk: sk.join("/") };
};

export const parseResourceIdFromSortKey = (item: { sk: string }) => {
  if (!item.sk.match(/(.*?):(.*?)\/(.*?)/)) {
    throw new Error(`Not a valid sort key from item: ${JSON.stringify(item)}`);
  }
  const [id, ...sk] = item.sk.split(":")[1].split("/");

  return {
    id,
    sk: sk.join("/"),
  };
};

export const parseResourceIdField = (item: { resourceId?: Maybe<string> }) => {
  if (!item.resourceId?.includes("/")) {
    console.log(item.resourceId);
    throw new Error(
      `Not a valid resource ID from item: ${JSON.stringify(item)}`,
    );
  }

  return parseResourceId(item.resourceId);
};

export const formatResourceIdFieldValue = (id: IDSK) => itemKey(id);

export const getPermalinkFromResource = (item: Resource) => {
  const resourceId = formatResourceIdFieldValue(item);
  const type = item.type;

  const url = new URL(getRestApiUrl());
  url.pathname = [
    url.pathname.replace(/\/$/, ""),
    "link",
    encodeURIComponent(resourceId),
    type,
  ]
    .filter(Boolean)
    .join("/");

  return url.toString();
};

export const getUserIdFromSortKey = (item: any) => {
  if (!item.sk.includes("#")) {
    throw new Error(`Not a valid sort key from item: ${JSON.stringify(item)}`);
  }
  return item.sk.split("#")[1];
};
